/**
 * 手机设备设置 Tab · Device-side settings tab body
 *
 * Embedded inside the `/settings#device` tab in [Settings.tsx](Settings.tsx).
 * The tab is the SPA-facing surface for the
 * `/api/admin/{process,restart,shutdown,config}` endpoints — it lets
 * the user see what's running on their machine (pid, version, uptime,
 * log path …), restart or stop the helper without dropping back to a
 * terminal, and edit the C-class `agent-local.json` overlay (port,
 * host, adb path, devices dir) with a one-click "save & restart"
 * button.
 *
 * The tab is conservative on purpose:
 *   - "立即重启" / "停止服务" both pop a confirm dialog before firing.
 *   - "保存并重启" first PUTs the config then triggers a restart;
 *     while the helper is bouncing we enter fast-poll so the SPA picks
 *     up the new process the moment it binds again.
 *   - 「一般设置」tab has more granular per-account preferences. This
 *     tab focuses on the C-tier fields tied to this specific PC.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  Copy,
  HardDriveDownload,
  Loader2,
  Network,
  Pause,
  Play,
  RefreshCw,
  Rocket,
  Save,
  XCircle,
} from 'lucide-react';
import clsx from 'clsx';
import { localServiceApi, serverInfoApi, type LocalServiceProcessInfo, type ServerInfo } from '@/lib/api';
import {
  useAgentConfig,
  useAgentConfigStore,
  useAgentConfigStatus,
  type AgentLocalConfig,
} from '@/lib/agentConfig';
import { useRuntimeConfig } from '@/lib/runtimeConfig';
import {
  AgentEndpointsCard,
  DeviceFilesCard,
  DiagnosticsCard,
  MirrorTimeoutCard,
  SdkRootCard,
  ToolsTableCard,
  WirelessAgentCard,
} from './localService/agentCards';

const POLL_INTERVAL_MS = 5_000;
/** procQuery snapshots older than this are treated as stale even if `data` exists. */
const STALE_THRESHOLD_MS = POLL_INTERVAL_MS * 3;
const LAUNCH_URL = 'adh://launch';
const FAST_POLL_TOTAL_MS = 30_000;

/**
 * Translate a raw mutation error into something the user can act on.
 *
 * Background: `localServiceApi.{restart,shutdown,updateConfig}` all
 * surface their network-layer failure as `TypeError: Failed to fetch`
 * (Chrome/Firefox/Safari all use the same wording). That message means
 * "the browser couldn't reach 127.0.0.1:10987" — the adh process is
 * gone, the port has been firewalled, or the SPA is pointed at a wrong
 * origin. We had a visible bug where ActionCard rendered the raw
 * English text alongside a still-shown "running" badge (stale cache),
 * which was completely opaque.
 *
 * The check covers the three common phrasings; everything else falls
 * through to the original message so genuine API errors aren't masked.
 */
export function humanizeAdhError(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  if (
    msg === 'Failed to fetch' ||
    /NetworkError/i.test(msg) ||
    /ERR_CONNECTION/i.test(msg) ||
    /Load failed/i.test(msg) /* Safari */
  ) {
    return '无法连接本机服务 (adh 已离线)。点上方「启动服务」按钮，或回到主页使用「一键启动」。';
  }
  return msg;
}

/**
 * The tab body. Renders inside `Settings.tsx`'s tab shell — no outer
 * page header / padding, those are owned by the shell.
 */
export function DeviceSettingsTab(): JSX.Element {
  const qc = useQueryClient();
  const enterFastPoll = useRuntimeConfig((s) => s.enterFastPoll);
  // The "ground truth" for adh reachability. `useRuntimeConfig`
  // polls `/api/config` and exposes `lastError`; when its kind is
  // 'network' we know the agent is unreachable. We refuse to render
  // a stale procQuery snapshot as "running" while this is the case.
  const runtimeInitialised = useRuntimeConfig((s) => s.initialised);
  const runtimeLastError = useRuntimeConfig((s) => s.lastError);
  const runtimeOnline = runtimeInitialised && runtimeLastError?.kind !== 'network';

  // Make sure agent config is loaded so the edit form has defaults.
  const initConfig = useAgentConfigStore((s) => s.init);
  useEffect(() => {
    void initConfig();
  }, [initConfig]);

  const procQuery = useQuery<LocalServiceProcessInfo, Error>({
    queryKey: ['admin.process'],
    queryFn: () => localServiceApi.process(),
    refetchInterval: POLL_INTERVAL_MS,
    // Survive transient errors silently — UI shows the last-known
    // snapshot when the next poll fails, plus a status badge.
    retry: 1,
    staleTime: 1_000,
    // Don't keep polling when adh is offline — the polls would all
    // fail and TanStack Query would keep displaying the cached
    // snapshot from the last good fetch. That's exactly the
    // "运行时长 3 分 17 秒 while actually offline 30 minutes" bug we
    // saw in the screenshot. When runtimeConfig comes back online,
    // re-enabling the query triggers an immediate refetch.
    enabled: runtimeOnline,
  });

  const proc = procQuery.data;
  // procQuery shows the last successful data even when subsequent
  // fetches fail (retry: 1 + staleTime). When the snapshot age
  // exceeds 3x the poll interval treat it as untrustworthy.
  const isStale =
    procQuery.dataUpdatedAt > 0 &&
    Date.now() - procQuery.dataUpdatedAt > STALE_THRESHOLD_MS;

  // Legacy-dir probe. `/api/server-info` reports whether `~/.adh` or
  // `~/Downloads/deviceManager` (the pre-`AndroidDeviceHub/` defaults)
  // are still present on disk. The check is a single existsSync per
  // request, so we can poll alongside `procQuery` without worry — but
  // we don't need it that fresh, hence the longer staleTime + no
  // refetchInterval. Disabled when adh is offline; the banner is only
  // useful when the user can act on it (and the migration script needs
  // adh stopped anyway).
  const serverInfoQuery = useQuery<ServerInfo, Error>({
    queryKey: ['admin.serverInfo'],
    queryFn: () => serverInfoApi.get(),
    enabled: runtimeOnline,
    staleTime: 30_000,
    retry: 1,
  });
  // Composite "really running" gate — used by both StatusCard and
  // ActionCard. All three signals must agree before we paint green.
  const showRunning = runtimeOnline && !!proc && !isStale;

  const restartMut = useMutation({
    mutationFn: () => localServiceApi.restart(),
    onSuccess: () => {
      // Service is about to exit; flip runtimeConfig into fast-poll so
      // we re-render the moment the trampoline child binds.
      enterFastPoll(30_000);
      // Drop cached process info — the next successful poll repopulates.
      qc.removeQueries({ queryKey: ['admin.process'] });
    },
  });

  const shutdownMut = useMutation({
    mutationFn: () => localServiceApi.shutdown(),
    onSuccess: () => {
      enterFastPoll(60_000);
      qc.removeQueries({ queryKey: ['admin.process'] });
    },
  });

  const handleLaunch = (): void => {
    // Trigger the OS protocol handler; runtimeConfig fast-poll picks
    // up the bound port as soon as the trampoline child opens it.
    enterFastPoll(FAST_POLL_TOTAL_MS);
    try {
      window.location.assign(LAUNCH_URL);
    } catch {
      /* sandboxed iframe — no-op; user can run `adh-desktop` manually */
    }
  };

  // Auto-launch hook —— 当用户被 ServiceGate 从 /devices 等 ADH-required
  // 路径 redirect 到这里时（`state.autoLaunch === true`），落地后立刻
  // fire 一次 adh://launch，省一次"启动服务"按钮点击。
  //
  // 仅在以下条件同时满足时触发：
  //   1. location.state.autoLaunch === true（redirect 携带的 hint）
  //   2. runtime 已经初始化（首轮 /api/config 已经回过）
  //   3. adh 当前确实离线（防止 polled-back-online 后还重复 fire）
  //   4. 本次 mount 还没自动启动过（ref 守卫，React.StrictMode 安全）
  //
  // fire 完立即把 state 清掉 —— 否则 F5 / back-forward 还会重新触发。
  const location = useLocation();
  const navigate = useNavigate();
  const autoLaunched = useRef(false);
  useEffect(() => {
    if (autoLaunched.current) return;
    const state = location.state as { autoLaunch?: boolean } | null;
    if (!state?.autoLaunch) return;
    if (!runtimeInitialised) return;
    if (runtimeOnline) {
      // 已经回线了，无需启动；顺手清掉 state 防止下次刷新乱触发
      navigate(location.pathname, { replace: true, state: null });
      return;
    }
    autoLaunched.current = true;
    const t = setTimeout(() => {
      handleLaunch();
      navigate(location.pathname, { replace: true, state: null });
    }, 300);
    return () => clearTimeout(t);
    // handleLaunch / location 等是 stable-enough；明确列依赖会过度触发
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runtimeInitialised, runtimeOnline]);

  return (
    <div className="space-y-4">
      <p className="text-xs text-ink-muted">
        管理这台电脑上的 adh 进程 —— 看运行状态、改启动参数、一键重启或停止；下方包含所有跟当前 hub 主机绑定的 agent 配置（C 类）。
      </p>

      <LegacyDirsBanner info={serverInfoQuery.data} />

      {!runtimeOnline ? <FirstInstallCard /> : null}

      <StatusCard
        proc={proc}
        loading={procQuery.isLoading}
        error={procQuery.error}
        runtimeOnline={runtimeOnline}
        showRunning={showRunning}
        isStale={isStale}
        snapshotAt={procQuery.dataUpdatedAt}
        onRefresh={() => procQuery.refetch()}
      />

      <ActionCard
        showRunning={showRunning}
        onRestart={() => restartMut.mutate()}
        onShutdown={() => shutdownMut.mutate()}
        onLaunch={handleLaunch}
        restartState={restartMut.status}
        shutdownState={shutdownMut.status}
        restartError={restartMut.error}
        shutdownError={shutdownMut.error}
      />

      <StartupConfigCard />

      <AgentConfigSection />
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* First-install card (offline only)                                */
/* ---------------------------------------------------------------- */

const INSTALL_CMD = 'npm i -g android-device-hub';
const SETUP_CMD = 'adh setup';

/**
 * Shown only while adh is unreachable. The SPA cannot run npm; this
 * card is the documented first-visit path: install globally, register
 * `adh://`, then use「启动服务」.
 */
function FirstInstallCard(): JSX.Element {
  return (
    <section className="overflow-hidden rounded-lg border border-accent/30 bg-accent/5">
      <header className="flex items-center gap-2 border-b border-accent/20 bg-accent/10 px-3 py-2">
        <HardDriveDownload className="h-4 w-4 text-accent" />
        <h2 className="text-sm font-semibold text-ink">首次使用：安装本机 adh</h2>
      </header>
      <div className="space-y-3 p-3 text-xs leading-relaxed text-ink-muted">
        <p>
          打开本站不会自动安装。需要本机 Node.js ≥ 18，在终端执行下面两步；装好后再点「启动服务」
          （或首页「一键启动」）即可通过 <code className="rounded bg-bg-soft px-1 text-ink">adh://</code> 拉起进程。
        </p>
        <ol className="list-decimal space-y-2 pl-4">
          <li>
            <span className="text-ink-dim">全局安装包</span>
            <CopyableCommand command={INSTALL_CMD} />
          </li>
          <li>
            <span className="text-ink-dim">注册协议并启动 helper</span>
            <CopyableCommand command={SETUP_CMD} />
          </li>
        </ol>
        <p className="text-[11px] text-ink-dim">
          已安装过但仍离线：跳过安装，直接点下方「启动服务」。升级：
          <code className="mx-1 rounded bg-bg-soft px-1 text-ink">npm i -g android-device-hub@latest && adh setup</code>
        </p>
      </div>
    </section>
  );
}

function CopyableCommand({ command }: { command: string }): JSX.Element {
  const [copied, setCopied] = useState(false);
  const onCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
      setTimeout(() => setCopied(false), 1_200);
    } catch {
      /* clipboard unavailable */
    }
  };
  return (
    <div className="mt-1 flex items-center gap-1.5 rounded-md border border-line bg-bg-card px-2 py-1.5 font-mono text-[11px] text-ink">
      <code className="min-w-0 flex-1 truncate">{command}</code>
      <button
        type="button"
        onClick={() => void onCopy()}
        className="inline-flex shrink-0 items-center gap-1 rounded border border-line px-1.5 py-0.5 text-[10px] text-ink-muted hover:bg-bg-soft hover:text-ink"
        title="复制"
      >
        {copied ? <CheckCircle2 className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3" />}
        {copied ? '已复制' : '复制'}
      </button>
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Legacy data-dir banner                                           */
/* ---------------------------------------------------------------- */

/**
 * Tells the user that pre-`AndroidDeviceHub/` data directories (`~/.adh`,
 * `~/Downloads/deviceManager`) are still on disk and points them at the
 * one-shot migration script.
 *
 * The check runs server-side (`/api/server-info` does a pair of
 * `existsSync` probes), so this is just rendering — no API call here.
 * Renders nothing when there's no data yet or when both flags are
 * false; we keep the JSX block out of the layout entirely so the
 * settings tab doesn't shift after first paint.
 */
function LegacyDirsBanner({ info }: { info: ServerInfo | undefined }): JSX.Element | null {
  if (!info) return null;
  if (!info.legacyDataDirExists && !info.legacyDevicesDirExists) return null;

  const stale: string[] = [];
  if (info.legacyDataDirExists) stale.push('~/.adh');
  if (info.legacyDevicesDirExists) stale.push('~/Downloads/deviceManager');

  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] text-amber-900 dark:border-amber-700/60 dark:bg-amber-900/20 dark:text-amber-200">
      <p className="flex items-center gap-1.5 font-medium">
        <AlertTriangle className="h-3.5 w-3.5" /> 检测到老版本数据目录残留
      </p>
      <p className="mt-1 text-amber-800 dark:text-amber-300/90">
        下列路径还在磁盘上：
        {stale.map((p) => (
          <code key={p} className="ml-1 rounded bg-amber-100 px-1 dark:bg-amber-900/40">{p}</code>
        ))}
        。当前 adh 已经切换到{' '}
        <code className="rounded bg-amber-100 px-1 dark:bg-amber-900/40">~/AndroidDeviceHub/</code>
        ，但你的配对信息 / secret / 截图还散落在老目录里。
      </p>
      <p className="mt-1">
        建议在终端里手动把里面的内容合并到{' '}
        <code className="rounded bg-amber-100 px-1 dark:bg-amber-900/40">~/AndroidDeviceHub/</code>
        ，确认无误后删除老目录即可。adh 不会自动迁移这些数据。
      </p>
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Agent C-class config section                                     */
/* ---------------------------------------------------------------- */

/**
 * Wraps the agent-local cards (SDK / host tools / devices / wireless /
 * mirror timeouts / endpoints / diagnostics) in a single section with
 * an offline banner. When adh is not running, the cards render their
 * own loading/empty states; we add a banner up top so the user knows
 * why nothing's interactive.
 */
function AgentConfigSection(): JSX.Element {
  const { ready } = useAgentConfigStatus();
  return (
    <div className="space-y-4">
      <div className="rounded-md border border-line bg-bg-soft px-3 py-2 text-[12px] text-ink-muted">
        <p className="font-medium text-ink">本机 adh 配置</p>
        <p className="mt-1">
          以下卡片对应 <code className="rounded bg-bg-card px-1 text-ink">agent-local.json</code>，只对当前 hub 主机生效。改完一般立即写入（端口、host、adb 路径除外，需要重启）。
        </p>
        {!ready && (
          <p className="mt-2 inline-flex items-center gap-1 text-[11px] text-amber-700 dark:text-amber-300">
            <AlertTriangle className="h-3 w-3" /> adh 离线 — 卡片只能读取上次缓存值，编辑请先启动 adh。
          </p>
        )}
      </div>
      <SdkRootCard />
      <ToolsTableCard />
      <DeviceFilesCard />
      <WirelessAgentCard />
      <MirrorTimeoutCard />
      <AgentEndpointsCard />
      <DiagnosticsCard />
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Status card                                                      */
/* ---------------------------------------------------------------- */

function StatusCard({
  proc,
  loading,
  error,
  runtimeOnline,
  showRunning,
  isStale,
  snapshotAt,
  onRefresh,
}: {
  proc: LocalServiceProcessInfo | undefined;
  loading: boolean;
  error: Error | null;
  runtimeOnline: boolean;
  showRunning: boolean;
  isStale: boolean;
  snapshotAt: number;
  onRefresh: () => void;
}): JSX.Element {
  return (
    <section className="card overflow-hidden">
      <header className="flex items-center justify-between gap-2 border-b border-line px-3 py-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <Network className="h-4 w-4 text-accent" />
          运行状态
        </h2>
        <div className="flex items-center gap-2">
          <StatusBadge
            runtimeOnline={runtimeOnline}
            showRunning={showRunning}
            isStale={isStale}
            loading={loading}
          />
          <button
            type="button"
            onClick={onRefresh}
            className="inline-flex items-center gap-1 rounded border border-line px-1.5 py-0.5 text-[11px] text-ink-muted hover:bg-bg-soft hover:text-ink"
            title="立即刷新"
          >
            <RefreshCw className="h-3 w-3" />
            刷新
          </button>
        </div>
      </header>
      <div className="p-3">
        {!runtimeOnline ? (
          // Network-class failure on /api/config — adh is unreachable.
          // Don't render the (stale) proc table; it would lie about
          // PID / uptime. Surface a clean offline banner instead so
          // the user understands why "启动服务" appears below.
          <div className="rounded-md border border-rose-500/30 bg-rose-500/5 p-3 text-xs text-rose-700 dark:text-rose-200">
            <p className="font-medium">本机服务无法连接 — adh 已离线</p>
            <p className="mt-1 text-rose-700/80 dark:text-rose-200/80">
              浏览器无法到达本机 adh 端口（默认 127.0.0.1:10987）。若从未安装，先看上方安装指引；已安装则点下方「启动服务」，或终端运行{' '}
              <code className="rounded bg-bg-card/40 px-1">adh-desktop</code>。
            </p>
          </div>
        ) : isStale && proc ? (
          // We have cached data but it's older than 3x the poll
          // interval — almost certainly because the last few polls
          // failed silently. Tell the user the snapshot is stale.
          <div className="mb-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-2 text-xs text-amber-800 dark:text-amber-200">
            <strong>状态过期：</strong>最后一次成功读取于 {formatAgo(snapshotAt)}。正在重试…
          </div>
        ) : null}
        {error && runtimeOnline ? (
          <div className="mb-2 rounded-md border border-rose-500/30 bg-rose-500/5 p-2 text-xs text-rose-700 dark:text-rose-200">
            读取状态失败：{humanizeAdhError(error)}
          </div>
        ) : null}
        {runtimeOnline && proc ? (
          <dl className="grid grid-cols-1 gap-x-4 gap-y-1.5 text-xs sm:grid-cols-2">
            <StatusRow label="PID" value={String(proc.pid)} mono />
            <StatusRow label="版本" value={`v${proc.version}`} />
            <StatusRow label="运行时长" value={formatUptime(proc.uptimeMs)} />
            <StatusRow
              label="启动时间"
              value={new Date(proc.startedAt).toLocaleString()}
            />
            <StatusRow label="监听" value={`${proc.host}:${proc.port ?? '?'}`} mono />
            <StatusRow label="Node" value={proc.nodeVersion} />
            <StatusRow label="平台" value={`${proc.platform} ${proc.arch}`} />
            <StatusRow label="数据目录" value={proc.dataDir} mono copyable />
            <StatusRow label="日志路径" value={proc.logPath} mono copyable />
            <StatusRow
              label="配置文件"
              value={proc.configFile ?? '（未生成，使用默认）'}
              mono
              copyable={!!proc.configFile}
            />
          </dl>
        ) : runtimeOnline && loading ? (
          <div className="flex items-center gap-2 text-xs text-ink-muted">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> 读取中…
          </div>
        ) : null}
      </div>
    </section>
  );
}

function StatusRow({
  label,
  value,
  mono,
  copyable,
}: {
  label: string;
  value: string;
  mono?: boolean;
  copyable?: boolean;
}): JSX.Element {
  const [copied, setCopied] = useState(false);
  const onCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1_200);
    } catch {
      /* clipboard unavailable in non-secure contexts */
    }
  };
  return (
    <div className="flex items-start justify-between gap-2 border-b border-line/50 py-1.5 last:border-b-0">
      <dt className="shrink-0 text-ink-muted">{label}</dt>
      <dd
        className={clsx(
          'min-w-0 flex-1 break-all text-right',
          mono ? 'font-mono text-[11px] text-ink' : 'text-ink',
        )}
      >
        {value}
        {copyable ? (
          <button
            type="button"
            onClick={onCopy}
            className="ml-1.5 inline-flex items-center align-middle text-ink-dim hover:text-ink"
            aria-label={`拷贝 ${label}`}
          >
            {copied ? (
              <CheckCircle2 className="h-3 w-3 text-emerald-500" />
            ) : (
              <Copy className="h-3 w-3" />
            )}
          </button>
        ) : null}
      </dd>
    </div>
  );
}

function StatusBadge({
  runtimeOnline,
  showRunning,
  isStale,
  loading,
}: {
  runtimeOnline: boolean;
  showRunning: boolean;
  isStale: boolean;
  loading: boolean;
}): JSX.Element {
  // 4 states with explicit priority:
  //   1. running        — runtime online + fresh proc snapshot
  //   2. offline        — runtime poll failed network (adh unreachable)
  //   3. stale (amber)  — runtime online but proc snapshot too old
  //   4. probing        — first paint, no data yet
  if (showRunning) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:text-emerald-300">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> 运行中
      </span>
    );
  }
  if (!runtimeOnline) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-rose-500/40 bg-rose-500/10 px-2 py-0.5 text-[10px] font-medium text-rose-700 dark:text-rose-300">
        <span className="h-1.5 w-1.5 rounded-full bg-rose-500" /> 已离线
      </span>
    );
  }
  if (isStale) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-300">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> 状态过期
      </span>
    );
  }
  if (loading) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-line bg-bg-soft px-2 py-0.5 text-[10px] text-ink-muted">
        <Loader2 className="h-3 w-3 animate-spin" /> 探测中
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-rose-500/40 bg-rose-500/10 px-2 py-0.5 text-[10px] font-medium text-rose-700 dark:text-rose-300">
      <span className="h-1.5 w-1.5 rounded-full bg-rose-500" /> 已停止
    </span>
  );
}

/* ---------------------------------------------------------------- */
/* Restart / shutdown card                                          */
/* ---------------------------------------------------------------- */

function ActionCard({
  showRunning,
  onRestart,
  onShutdown,
  onLaunch,
  restartState,
  shutdownState,
  restartError,
  shutdownError,
}: {
  showRunning: boolean;
  onRestart: () => void;
  onShutdown: () => void;
  onLaunch: () => void;
  restartState: 'idle' | 'pending' | 'success' | 'error';
  shutdownState: 'idle' | 'pending' | 'success' | 'error';
  restartError: Error | null;
  shutdownError: Error | null;
}): JSX.Element {
  const inflight = restartState === 'pending' || shutdownState === 'pending';
  const confirmAndRun = (action: 'restart' | 'shutdown', cb: () => void) => {
    const msg =
      action === 'restart'
        ? '确定要立即重启本机服务吗？\n所有 ADB 流、镜像、终端会话会被中断；服务通常在 1~2 秒内重新可用。'
        : '确定要停止本机服务吗？\n停止后页面会回到「本地服务尚未启动」屏，你需要再次点「启动服务」才能恢复。';
    if (window.confirm(msg)) cb();
  };
  return (
    <section className="card overflow-hidden">
      <header className="flex items-center justify-between gap-2 border-b border-line px-3 py-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <RefreshCw className="h-4 w-4 text-accent" />
          生命周期
        </h2>
      </header>
      <div className="space-y-3 p-3">
        {/*
          Two distinct UI shapes here, not a single button bar with
          disabled states. Greyed-out "立即重启" + raw "Failed to fetch"
          is exactly the confusion path we're moving away from — when
          the agent is unreachable, the only correct action is "拉起来".
        */}
        {showRunning ? (
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={inflight}
              onClick={() => confirmAndRun('restart', onRestart)}
              className={clsx(
                'inline-flex items-center gap-1.5 rounded-md border border-accent/40 bg-accent/10 px-3 py-1.5 text-xs font-medium text-accent transition',
                'hover:bg-accent/15 disabled:cursor-not-allowed disabled:opacity-50',
              )}
            >
              {restartState === 'pending' ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Play className="h-3.5 w-3.5" />
              )}
              立即重启
            </button>
            <button
              type="button"
              disabled={inflight}
              onClick={() => confirmAndRun('shutdown', onShutdown)}
              className={clsx(
                'inline-flex items-center gap-1.5 rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-1.5 text-xs font-medium text-rose-700 transition dark:text-rose-300',
                'hover:bg-rose-500/15 disabled:cursor-not-allowed disabled:opacity-50',
              )}
            >
              {shutdownState === 'pending' ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Pause className="h-3.5 w-3.5" />
              )}
              停止服务
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={onLaunch}
              className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-xs font-semibold text-white shadow-card transition hover:bg-accent/90"
            >
              <Rocket className="h-3.5 w-3.5" />
              启动服务
            </button>
            <span className="text-[11px] text-ink-muted">
              触发 OS 协议处理器 <code className="rounded bg-bg-soft px-1 text-ink">adh://launch</code>
              ；需先完成上方安装并执行 <code className="rounded bg-bg-soft px-1 text-ink">adh setup</code>
              。首次使用会弹浏览器协议确认框。
            </span>
          </div>
        )}
        {restartError ? (
          <ActionError label="重启失败" message={humanizeAdhError(restartError)} />
        ) : null}
        {shutdownError ? (
          <ActionError label="停止失败" message={humanizeAdhError(shutdownError)} />
        ) : null}
        <p className="text-[11px] leading-relaxed text-ink-dim">
          重启使用 trampoline 子进程 —— 当前进程立刻退出，约 1.2 秒后新进程在同一端口启动。停止后页面会自动跳回离线态，再次启动可点上方「启动服务」或在终端运行 <code className="rounded bg-bg-soft px-1 text-ink">adh-desktop</code>。
        </p>
      </div>
    </section>
  );
}

function ActionError({
  label,
  message,
}: {
  label: string;
  message: string;
}): JSX.Element {
  return (
    <div className="flex items-start gap-2 rounded-md border border-rose-500/30 bg-rose-500/5 p-2 text-xs text-rose-700 dark:text-rose-200">
      <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span>
        <strong>{label}：</strong>
        {message}
      </span>
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Startup config card                                              */
/* ---------------------------------------------------------------- */

function StartupConfigCard(): JSX.Element {
  const cfg = useAgentConfig();
  const qc = useQueryClient();
  const enterFastPoll = useRuntimeConfig((s) => s.enterFastPoll);

  // Local form draft — copied from store on mount + on cfg reload.
  const [draft, setDraft] = useState(() => makeDraft(cfg));
  useEffect(() => {
    setDraft(makeDraft(cfg));
  }, [cfg]);

  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(makeDraft(cfg)),
    [draft, cfg],
  );

  const saveAndRestartMut = useMutation({
    mutationFn: async () => {
      // devices.dir intentionally NOT included here — it's managed by
      // the DeviceFilesCard below (debounced, immediate effect, no
      // restart needed). Mixing it in here would cause a redundant
      // restart-on-edit experience.
      const patch: Record<string, unknown> = {
        web: { host: draft.host, port: Number(draft.port) },
        adb: { path: draft.adbPath },
      };
      await localServiceApi.updateConfig(patch);
      await localServiceApi.restart();
    },
    onSuccess: () => {
      enterFastPoll(30_000);
      qc.removeQueries({ queryKey: ['admin.process'] });
    },
  });

  return (
    <section className="card overflow-hidden">
      <header className="flex items-center justify-between gap-2 border-b border-line px-3 py-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <HardDriveDownload className="h-4 w-4 text-accent" />
          启动参数
        </h2>
        <span className="rounded-full border border-sky-500/40 bg-sky-500/10 px-1.5 py-px text-[10px] font-medium uppercase tracking-wider text-sky-700 dark:text-sky-300">
          这台电脑
        </span>
      </header>
      <div className="space-y-3 p-3 text-xs">
        <p className="text-[11px] leading-relaxed text-ink-muted">
          这些字段写入 <code className="rounded bg-bg-soft px-1 text-ink">agent-local.json</code>，**保存即重启**才生效。设置页里的细分项（SDK / 镜像超时 / 网络）走防抖立即生效。
        </p>

        <FormRow label="监听端口" hint="adh HTTP/WS 服务端口；默认 10987">
          <input
            type="number"
            value={draft.port}
            onChange={(e) => setDraft({ ...draft, port: e.target.value })}
            className="w-32 rounded border border-line bg-bg-card px-2 py-1 font-mono text-xs text-ink"
            min={1}
            max={65535}
          />
        </FormRow>

        <FormRow label="监听 host" hint="127.0.0.1 = 仅本机；0.0.0.0 = 同局域网可访问">
          <input
            type="text"
            value={draft.host}
            onChange={(e) => setDraft({ ...draft, host: e.target.value })}
            className="w-48 rounded border border-line bg-bg-card px-2 py-1 font-mono text-xs text-ink"
            placeholder="127.0.0.1"
          />
        </FormRow>

        <FormRow label="ADB 路径" hint="留 'adb' 走 PATH 解析；或绝对路径指定">
          <input
            type="text"
            value={draft.adbPath}
            onChange={(e) => setDraft({ ...draft, adbPath: e.target.value })}
            className="w-full max-w-md rounded border border-line bg-bg-card px-2 py-1 font-mono text-xs text-ink"
            placeholder="adb"
          />
        </FormRow>

        <p className="text-[11px] text-ink-dim">
          抓取目录、SDK 根、host tools、wireless / 镜像超时等 C 类配置在下方独立卡片里改（防抖立即生效，无需重启）。
        </p>

        <div className="flex flex-wrap items-center gap-2 pt-2">
          <button
            type="button"
            disabled={!dirty || saveAndRestartMut.status === 'pending'}
            onClick={() => {
              if (
                window.confirm(
                  '保存并重启后，服务会在新参数下重新监听。\n所有 ADB 流、镜像会话会被中断。继续？',
                )
              ) {
                saveAndRestartMut.mutate();
              }
            }}
            className={clsx(
              'inline-flex items-center gap-1.5 rounded-md border border-accent/40 bg-accent/10 px-3 py-1.5 text-xs font-medium text-accent transition',
              'hover:bg-accent/15 disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            {saveAndRestartMut.status === 'pending' ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Save className="h-3.5 w-3.5" />
            )}
            保存并重启
          </button>
          {dirty ? (
            <button
              type="button"
              onClick={() => setDraft(makeDraft(cfg))}
              className="inline-flex items-center gap-1 rounded border border-line px-2 py-1 text-[11px] text-ink-muted hover:bg-bg-soft hover:text-ink"
            >
              重置
            </button>
          ) : (
            <span className="text-[10px] text-ink-dim">没有未保存的改动</span>
          )}
          {saveAndRestartMut.error ? (
            <ActionError
              label="保存失败"
              message={humanizeAdhError(saveAndRestartMut.error)}
            />
          ) : null}
        </div>

        <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-2 text-[11px] leading-relaxed text-amber-800 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
          <span>
            改完端口后，浏览器下次访问的 API 基地址会跟着切。SPA 默认连 <code className="rounded bg-bg-card/40 px-1">127.0.0.1:10987</code>，端口改了之后用
            <code className="rounded bg-bg-card/40 px-1">window.__ADH_API_ORIGIN__</code> 或 <code className="rounded bg-bg-card/40 px-1">ADH_HUB_URL</code> 同步配上。
          </span>
        </div>
      </div>
    </section>
  );
}

function FormRow({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:gap-3">
      <div className="w-full shrink-0 pt-1.5 sm:w-32">
        <label className="text-xs text-ink">{label}</label>
        {hint ? <div className="text-[10px] leading-snug text-ink-dim">{hint}</div> : null}
      </div>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Helpers                                                          */
/* ---------------------------------------------------------------- */

interface DraftConfig {
  port: string;
  host: string;
  adbPath: string;
}

function makeDraft(cfg: AgentLocalConfig): DraftConfig {
  return {
    port: String(cfg.web.port),
    host: cfg.web.host,
    adbPath: cfg.adb.path,
  };
}

/**
 * Render the difference between `ts` and now as a human-readable
 * Chinese phrase. Used by the StatusCard "状态过期" banner to tell
 * users how old the last good snapshot is.
 */
function formatAgo(ts: number): string {
  const ms = Date.now() - ts;
  if (ms < 1_000) return '刚刚';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s} 秒前`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} 分 ${s % 60} 秒前`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} 小时 ${m % 60} 分前`;
  const d = Math.floor(h / 24);
  return `${d} 天 ${h % 24} 小时前`;
}

function formatUptime(ms: number): string {
  if (ms < 1_000) return `${ms} ms`;
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s} 秒`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} 分 ${s % 60} 秒`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} 小时 ${m % 60} 分`;
  const d = Math.floor(h / 24);
  return `${d} 天 ${h % 24} 小时`;
}
